import React, { useEffect, useMemo } from 'react';
import Checkbox from '@mui/material/Checkbox';
import Button from '../../BaseComponents/Button';
import { useFormikContext } from 'formik';
import { StepProps } from '../../../pages/complete-profile';
import {
  getOnboardingDeclarationItems,
  getOnboardingTotalSteps,
  isScanOrPlusMembership,
  OnboardingFormValues,
  shouldShowFemaleSexHormonesStep
} from '../../../types/membersTypes';
import './FormQuestion.scss';

type DeclarationStepProps = {
  setCurrentStep: (step: StepProps) => void;
  saveStepData?: (stepNumber: number) => Promise<void>;
  populateFormWithSavedData?: () => void;
  isLoading?: boolean;
  membershipType?: string | null;
};

const DeclarationStep: React.FC<DeclarationStepProps> = ({
  setCurrentStep,
  saveStepData,
  populateFormWithSavedData,
  isLoading,
  membershipType
}) => {
  const { values, setFieldValue, setFieldError, errors } =
    useFormikContext<OnboardingFormValues>();
  const isFemale = values.gender?.key === 'female';
  const isScanOrPlus = isScanOrPlusMembership(membershipType);
  const declarationItems = useMemo(
    () => getOnboardingDeclarationItems(isFemale, membershipType),
    [isFemale, membershipType]
  );

  const [hasPopulated, setHasPopulated] = React.useState(false);
  const [localError, setLocalError] = React.useState('');

  useEffect(() => {
    if (populateFormWithSavedData && !hasPopulated) {
      populateFormWithSavedData();
      setHasPopulated(true);
    }
  }, [populateFormWithSavedData, hasPopulated]);

  useEffect(() => {
    const current = values.disclaimer_confirmations || {};
    const next = { ...current };
    let changed = false;
    declarationItems.forEach((item) => {
      if (next[item] === undefined) {
        next[item] = false;
        changed = true;
      }
    });
    if (changed) {
      setFieldValue('disclaimer_confirmations', next);
    }
  }, [declarationItems, setFieldValue, values.disclaimer_confirmations]);

  const allChecked = declarationItems.every(
    (item) => values.disclaimer_confirmations?.[item] === true
  );

  const handleSubmit = async () => {
    if (!allChecked) {
      setLocalError('Please tick all declaration checkboxes to continue');
      setFieldError(
        'disclaimer_confirmations',
        'Please tick all declaration checkboxes to continue'
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setLocalError('');
    if (!saveStepData) return;

    try {
      await saveStepData(getOnboardingTotalSteps(membershipType, isFemale));
    } catch {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className='step-3'>
      <div className='section-header'>Declaration</div>
      <div className='confirm-information-checkboxes'>
        {declarationItems.map((item, index) => {
          const checkboxId = `onboarding-declaration-${index}`;
          return (
            <div key={item} className='confirm-information-checkbox'>
              <div className='d-flex align-items-center'>
                <Checkbox
                  id={checkboxId}
                  checked={values.disclaimer_confirmations?.[item] || false}
                  onChange={(e) => {
                    setFieldValue('disclaimer_confirmations', {
                      ...values.disclaimer_confirmations,
                      [item]: e.target.checked
                    });
                    setLocalError('');
                    setFieldError('disclaimer_confirmations', undefined);
                  }}
                />
                <label htmlFor={checkboxId}>
                  {item}
                  <span className='required'> *</span>
                </label>
              </div>
            </div>
          );
        })}
      </div>
      {(localError || errors.disclaimer_confirmations) && (
        <div className='error-message'>
          {localError || String(errors.disclaimer_confirmations || '')}
        </div>
      )}

      <div className='submit-btn-wrapper'>
        <Button
          text='Back'
          onClick={() => {
            const hasHormonesStep = shouldShowFemaleSexHormonesStep(isFemale);
            setCurrentStep({
              number: isScanOrPlus
                ? hasHormonesStep
                  ? 6
                  : 5
                : hasHormonesStep
                  ? 5
                  : 4,
              name: 'Goals'
            });
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          backgroundColor={'#ffffff'}
          color={'#2D334E'}
        />
        <Button
          text='Submit'
          isDisabled={isLoading || !allChecked}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};

export default DeclarationStep;
